import { EventBus } from '@antification/core';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  unlocked: boolean;
  unlockedAt?: number;
  hidden?: boolean; // Don't show until unlocked
}

export class AchievementManager {
  private events: EventBus;
  private achievements: Map<string, Achievement> = new Map();

  // Tracking stats
  private bottomScreenViewed = false;
  private antsNamed = 0;
  private antWatchedDie = false;
  private watchedSurvivorsTime = 0;
  private colonyExtinct = false;
  private parkingLotComplete = false;

  constructor(events: EventBus) {
    this.events = events;
    this.initializeAchievements();
    this.setupListeners();
  }

  private initializeAchievements(): void {
    const achievementDefs: Omit<Achievement, 'unlocked'>[] = [
      {
        id: 'efficient_use',
        name: 'Efficient Use of Space',
        description: 'Complete your first parking lot.',
      },
      {
        id: 'under_budget',
        name: 'Under Budget',
        description: 'Complete a parking lot without any delays.',
      },
      {
        id: 'colony_what_colony',
        name: 'Colony? What Colony?',
        description: 'Complete a parking lot without ever viewing the bottom screen.',
        hidden: true,
      },
      {
        id: 'witness',
        name: 'Witness',
        description: 'Watch an ant die.',
      },
      {
        id: 'kira',
        name: 'Kira',
        description: 'Name an ant. Then complete the parking lot.',
      },
      {
        id: 'the_crack',
        name: 'The Crack',
        description: 'Observe the surviving colony for 30 minutes post-construction.',
      },
      {
        id: 'optimal_steady_state',
        name: 'Optimal Steady State',
        description: 'The ant population has stabilized.',
        hidden: true,
      },
      {
        id: 'prime_location',
        name: 'Prime Location',
        description: '100% completion.',
      },
      {
        id: 'old_map',
        name: 'Old Map',
        description: 'Witness the passing of an elder.',
        hidden: true,
      },
      {
        id: 'philosopher',
        name: 'The Philosopher',
        description: 'Find the ant who thinks.',
        hidden: true,
      },
      {
        id: 'thank_you',
        name: 'Thank You For Your Business',
        description: 'Parkington Development Suite thanks you for your contribution.',
      },
    ];

    for (const def of achievementDefs) {
      this.achievements.set(def.id, { ...def, unlocked: false });
    }

    // Load from localStorage if available
    this.load();
  }

  private setupListeners(): void {
    // Bottom screen viewed
    this.events.on('BOTTOM_SCREEN_VIEWED', () => {
      this.bottomScreenViewed = true;
    });

    // Ant named
    this.events.on('ANT_NAMED', () => {
      this.antsNamed++;
    });

    // Ant died while viewing
    this.events.on('ANT_DIED', (payload) => {
      if (this.bottomScreenViewed) {
        this.antWatchedDie = true;
        this.unlock('witness');
      }

      // Check for Old Map achievement
      const data = payload as { name: string };
      if (data.name === 'Old Map') {
        this.unlock('old_map');
      }
    });

    // Colony extinct
    this.events.on('COLONY_EXTINCT', () => {
      this.colonyExtinct = true;
      this.unlock('optimal_steady_state');
    });

    // Parking lot complete
    this.events.on('PARKING_LOT_COMPLETE', () => {
      this.parkingLotComplete = true;

      this.unlock('efficient_use');
      this.unlock('prime_location');
      this.unlock('thank_you');

      // Colony? What Colony?
      if (!this.bottomScreenViewed) {
        this.unlock('colony_what_colony');
      }

      // Kira achievement
      if (this.antsNamed > 0) {
        this.unlock('kira');
      }
    });
  }

  unlock(id: string): boolean {
    const achievement = this.achievements.get(id);
    if (!achievement || achievement.unlocked) {
      return false;
    }

    achievement.unlocked = true;
    achievement.unlockedAt = Date.now();

    // Emit event for notification
    this.events.emit('ACHIEVEMENT_UNLOCKED', {
      name: achievement.name,
      description: achievement.description,
    });

    // Save to localStorage
    this.save();

    return true;
  }

  // Called each frame when post-construction and viewing colony
  updateSurvivorWatchTime(dt: number, isViewingColony: boolean, parkingComplete: boolean): void {
    if (parkingComplete && isViewingColony) {
      this.watchedSurvivorsTime += dt;

      // 30 minutes = 1800000 ms
      if (this.watchedSurvivorsTime >= 1800000) {
        this.unlock('the_crack');
      }
    }
  }

  // Check for philosopher ant
  checkPhilosopher(antIsContemplating: boolean): void {
    if (antIsContemplating && this.bottomScreenViewed) {
      this.unlock('philosopher');
    }
  }

  getAchievement(id: string): Achievement | undefined {
    return this.achievements.get(id);
  }

  getAllAchievements(): Achievement[] {
    return Array.from(this.achievements.values());
  }

  getUnlockedCount(): number {
    return Array.from(this.achievements.values()).filter((a) => a.unlocked).length;
  }

  getTotalCount(): number {
    return this.achievements.size;
  }

  private save(): void {
    try {
      const data = {
        achievements: Array.from(this.achievements.entries()).map(([id, a]) => ({
          id,
          unlocked: a.unlocked,
          unlockedAt: a.unlockedAt,
        })),
      };
      localStorage.setItem('parking-lot-achievements', JSON.stringify(data));
    } catch (e) {
      console.warn('Could not save achievements:', e);
    }
  }

  private load(): void {
    try {
      const stored = localStorage.getItem('parking-lot-achievements');
      if (stored) {
        const data = JSON.parse(stored);
        for (const item of data.achievements) {
          const achievement = this.achievements.get(item.id);
          if (achievement) {
            achievement.unlocked = item.unlocked;
            achievement.unlockedAt = item.unlockedAt;
          }
        }
      }
    } catch (e) {
      console.warn('Could not load achievements:', e);
    }
  }
}
